"use client";

import { chipsByCountry, type BoardAreaChip } from "./board-area-model";

/**
 * Every generic board other than surf and piste: area chips that filter the rows in place
 * (forecast areas spec, section 10.2). The choice lives in the shallow `?area=` param, applied
 * after hydration, so the server HTML and the canonical stay the full board. `#area=` deep links
 * arrive through the conditions fragment lens. The chips are toggle buttons (`aria-pressed`) in a
 * labelled group, not a radiogroup, so each is an ordinary tab stop with no arrow-key contract.
 */
export function BoardAreaFilter({
  chips,
  activeArea,
  activeAreaName,
  onSelect,
}: {
  chips: readonly BoardAreaChip[];
  activeArea: string | null;
  /** Name of the active area, which may not have a chip (a small area linked from a spot page). */
  activeAreaName: string | null;
  onSelect: (areaSlug: string | null) => void;
}) {
  if (chips.length === 0 && !activeArea) return null;
  const base = "min-h-11 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal";
  const on = "bg-navy text-white border border-navy";
  const off = "bg-white border border-border-light text-text-body hover:border-navy/40 hover:text-navy";
  const activeHasChip = activeArea !== null && chips.some((chip) => chip.slug === activeArea);
  return (
    <div data-board-area-filter>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[1.2px] text-text-muted">
        Filter by area
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-1" role="group" aria-label="Area filter">
        <button
          type="button"
          aria-pressed={activeArea === null}
          onClick={() => onSelect(null)}
          className={`${base} ${activeArea === null ? on : off}`}
        >
          All areas
        </button>
        {activeArea !== null && !activeHasChip && (
          <button type="button" aria-pressed onClick={() => onSelect(activeArea)} className={`${base} ${on}`}>
            {activeAreaName ?? activeArea}
          </button>
        )}
        {chipsByCountry(chips).map((group) => (
          <div key={group.countryCode} className="flex shrink-0 items-center gap-2" role="presentation">
            <span className="whitespace-nowrap pl-1 text-[11px] font-semibold text-text-muted" aria-hidden>
              {group.countryName}
            </span>
            {group.chips.map((chip) => (
              <button
                key={chip.slug}
                type="button"
                aria-pressed={activeArea === chip.slug}
                aria-label={`${chip.name}, ${group.countryName}`}
                onClick={() => onSelect(chip.slug)}
                className={`${base} ${activeArea === chip.slug ? on : off}`}
              >
                {chip.name}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
